/* @flow weak */
import cx from 'classnames';
import * as React from 'react';
import { WithRouterProps } from 'react-router';

import Button from 'components/Button';
import Columns2 from 'components/Columns2';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import DatePickerInput from 'components/DatePickerInput';
import Field from 'components/Field';
import Fieldset from 'components/Fieldset';
import Label from 'components/Label';
import Legend from 'components/Legend';
import MessageActions from 'groups/actions/MessageActions';
import Modal from 'react-modal';
import Select from 'components/Select';
import { ALL_TOKENS_ITEM } from 'components/Tokenizer';
import { withRouter } from 'app/context/Router';
import SETTINGS_ROUTES from 'settings/constants/SettingsRouteConstants';

import type { LoadOwnedGroupSummaries_me_classes } from 'groups/queries/__generated__/LoadOwnedGroupSummaries';

import SettingsContainer from './SettingsContainer';

const ALL_CLASSES_OPTION = { label: 'All classes', value: ALL_TOKENS_ITEM };

type Props = {
  groups: LoadOwnedGroupSummaries_me_classes[],
} & WithRouterProps;

class PrintAnnouncements extends React.Component<Props, $FlowFixMeState> {
  constructor(props: Props, context) {
    super(props, context);
    const startDate = this.sixMonthsAgo();
    const endDate = new Date();

    this.state = {
      group: ALL_CLASSES_OPTION,
      startDate,
      endDate,
      groupOptions: this.getFormattedGroupSelectOptions(),
      showEmailRequiredModal: false,
    };
  }

  getFormattedGroupSelectOptions = () => {
    return [ALL_CLASSES_OPTION].concat(
      this.props.groups.map(group => {
        return { label: group.class_name, value: group.id };
      })
    );
  };

  setSelectedGroup = value => {
    const group = this.state.groupOptions.find(option => option.value === value);
    this.setState({ group });
  };

  canSubmit = () => {
    return (
      this.state.group !== null &&
      this.state.startDate !== null &&
      this.state.endDate !== null &&
      this.state.startDate < this.state.endDate
    );
  };

  handleEndDateChange = date => {
    this.setState({ endDate: date });
  };

  handleStartDateChange = date => {
    this.setState({ startDate: date });
  };

  printAnnouncements = (): void => {
    if (!CurrentUser.hasEmail()) {
      this.toggleShowEmailRequiredModal();
      return;
    }

    let groupIds;

    // if selected group is all class pass an empty array.
    const allGroups = ALL_CLASSES_OPTION.value;
    if (this.state.group.value === allGroups) {
      groupIds = [];
    } else {
      groupIds = [this.state.group.value];
    }

    MessageActions.export(groupIds, this.state.startDate, this.state.endDate);
  };

  toggleShowEmailRequiredModal = () => {
    this.setState({
      showEmailRequiredModal: !this.state.showEmailRequiredModal,
    });
  };

  sixMonthsAgo = () => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return sixMonthsAgo;
  };

  render() {
    const today = new Date();

    return (
      <div className="PrintAnnouncements PrintSettingSection">
        <Modal isOpen={this.state.showEmailRequiredModal}>
          <ConfirmDialog
            title="Add an email address to your account first"
            subtext="Transcripts are sent to your email. You can add an email address in your account settings."
            actionButtonText="Go to Settings"
            cancelButtonText="Cancel"
            onConfirm={() => this.props.router.push(SETTINGS_ROUTES.NOTIFICATIONS)}
            onCancel={this.toggleShowEmailRequiredModal}
            onRequestHide={this.toggleShowEmailRequiredModal}
            eventKey="print_announcements.no_email_modal.view"
          />
        </Modal>
        <Fieldset>
          <Legend>
            Announcements
            <p>Export a PDF of all the announcements you’ve sent to participants in a class.</p>
          </Legend>

          <SettingsContainer>
            <Field>
              <Columns2 gutter="small">
                <div>
                  <Label>Class</Label>
                  <Select
                    className="PrintSettingSection-groupSelector"
                    options={this.state.groupOptions}
                    value={this.state.group.value}
                    onChange={this.setSelectedGroup}
                  />
                </div>

                <div>
                  <Columns2 gutter="small">
                    <div>
                      <Label>Start date</Label>
                      <DatePickerInput
                        className={cx('control input')}
                        error={this.state.startDate > today}
                        full
                        ref="startdate"
                        value={this.state.startDate}
                        max={today}
                        onChange={this.handleStartDateChange}
                      />
                    </div>

                    <div>
                      <Label>End date</Label>
                      <DatePickerInput
                        className={cx('control input')}
                        error={this.state.startDate >= this.state.endDate}
                        full
                        ref="endDate"
                        value={this.state.endDate}
                        min={this.state.startDate}
                        max={today}
                        onChange={this.handleEndDateChange}
                      />
                    </div>
                  </Columns2>
                </div>
              </Columns2>
            </Field>
          </SettingsContainer>

          <Button
            data-test="export-announcements-button"
            size="small"
            disabled={!this.canSubmit()}
            onClick={this.printAnnouncements}>
            Export
          </Button>
        </Fieldset>
      </div>
    );
  }
}

export { PrintAnnouncements };
export default withRouter(PrintAnnouncements);
